import { crashReports, startCrashLog, type CrashLog, type CrashSample } from "./log";
import { sendPendingReports } from "./upload";

const SAMPLE_EVERY_MS = 2_000;
const BYTES_PER_MB = 1_048_576;
/** Portraits are published at this size and drawn as CSS backgrounds, which report no size. */
const PORTRAIT_PIXELS = 256 * 256;

function megabytes(pixels: number): number {
  return Math.round((pixels * 4) / BYTES_PER_MB);
}

function measurePage(startMs: number, fps: number, worstFrameMs: number): CrashSample {
  const images = Array.from(document.images);
  const faces = new Set(
    Array.from(document.querySelectorAll<HTMLElement>('[style*="background-image"]'))
      .map((element) => element.style.backgroundImage),
  ).size;
  const imagePixels = images.reduce((sum, img) => sum + img.naturalWidth * img.naturalHeight, 0)
    + faces * PORTRAIT_PIXELS;
  const canvases = Array.from(document.getElementsByTagName("canvas"));
  const memory = (performance as { memory?: { usedJSHeapSize: number } }).memory;
  return {
    at: Math.round((performance.now() - startMs) / 1000),
    path: location.pathname + location.search,
    scene: document.querySelector(".timeline-scene-overlay__title")?.textContent ?? "",
    fps,
    worstFrameMs: Math.round(worstFrameMs),
    domNodes: document.getElementsByTagName("*").length,
    images: images.length + faces,
    imagesFetched: performance.getEntriesByType("resource")
      .filter((entry) => (entry as PerformanceResourceTiming).initiatorType === "css"
        || (entry as PerformanceResourceTiming).initiatorType === "img").length,
    imageMb: megabytes(imagePixels),
    canvasMb: megabytes(canvases.reduce((sum, item) => sum + item.width * item.height, 0)),
    heapMb: memory === undefined ? null : Math.round(memory.usedJSHeapSize / BYTES_PER_MB),
    visible: document.visibilityState === "visible",
  };
}

function watchFrames(): () => { fps: number; worstFrameMs: number } {
  let frames = 0;
  let worst = 0;
  let last = performance.now();
  let windowStart = last;
  const tick = (now: number): void => {
    frames += 1;
    worst = Math.max(worst, now - last);
    last = now;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
  return () => {
    const now = performance.now();
    const seconds = Math.max(0.001, (now - windowStart) / 1000);
    const result = { fps: Math.round(frames / seconds), worstFrameMs: worst };
    frames = 0;
    worst = 0;
    windowStart = now;
    return result;
  };
}

function watchEvents(log: CrashLog, startMs: number): void {
  const note = (text: string): void => {
    log.event(`${Math.round((performance.now() - startMs) / 1000)}s ${text.slice(0, 300)}`);
  };
  window.addEventListener("error", (event) => note(`error: ${event.message}`));
  window.addEventListener("unhandledrejection", (event) => note(`rejected: ${String(event.reason)}`));
  // Graphics loss does not bubble, so catch it on the way down.
  window.addEventListener("webglcontextlost", () => note("WebGL context lost"), true);
  window.addEventListener("webglcontextrestored", () => note("WebGL context restored"), true);
  document.addEventListener("visibilitychange", () => note(`page ${document.visibilityState}`));
}

/** Records on the device. Only a device switched on at /status sends its crashes anywhere. */
export function startCrashRecorder(build: string): void {
  let storage: Storage;
  try {
    storage = window.localStorage;
  } catch {
    return;
  }
  const startMs = performance.now();
  performance.setResourceTimingBufferSize(5_000);
  const log = startCrashLog(storage, {
    startedAt: new Date().toISOString(),
    build,
    device: navigator.userAgent,
    screen: `${innerWidth}x${innerHeight} @${devicePixelRatio}x`,
  });
  watchEvents(log, startMs);
  void sendPendingReports(crashReports(storage), storage);
  const frames = watchFrames();
  const record = (): void => {
    const { fps, worstFrameMs } = frames();
    log.sample(measurePage(startMs, fps, worstFrameMs));
  };
  window.setInterval(record, SAMPLE_EVERY_MS);
  window.addEventListener("pagehide", () => log.end());
  window.addEventListener("pageshow", () => log.resume());
  document.addEventListener("visibilitychange", record);
}
